import React from 'react';
import { Box, FormControl, MenuItem, Select, SelectChangeEvent, Typography } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import type { ContestSelectOption } from './contestSelectOptions';
import { editorialLabelSx, midnight } from './midnightStyles';

interface Props {
    label?: string;
    value: string;
    options: ContestSelectOption[];
    onChange: (value: string) => void;
    disabled?: boolean;
    placeholder?: string;
}

function OptionRow({ option, selected }: { option: ContestSelectOption; selected: boolean }) {
    return (
        <Box
            sx={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1,
                minWidth: 0,
            }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0, flex: 1 }}>
                <Box
                    sx={{
                        width: 34,
                        height: 34,
                        borderRadius: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        bgcolor: selected ? midnight.primary : '#0e0e0e',
                        color: selected ? '#392700' : midnight.onSurface,
                        border: selected ? 'none' : '1px solid rgba(255,255,255,0.08)',
                        '& .MuiSvgIcon-root': { fontSize: 19 },
                    }}>
                    {option.icon}
                </Box>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography
                        noWrap
                        sx={{
                            fontWeight: 700,
                            fontSize: 15,
                            lineHeight: 1.15,
                            color: selected ? midnight.primary : midnight.onSurface,
                        }}>
                        {option.label}
                    </Typography>
                    {option.subtitle ? (
                        <Typography
                            noWrap
                            sx={{
                                fontSize: 11,
                                mt: 0.25,
                                color: midnight.onSurfaceVariant,
                            }}>
                            {option.subtitle}
                        </Typography>
                    ) : null}
                </Box>
            </Box>
            {selected ? <CheckCircleIcon sx={{ color: midnight.primary, fontSize: 18, flexShrink: 0 }} /> : null}
        </Box>
    );
}

export function ContestRichSelect({ label, value, options, onChange, disabled, placeholder }: Props) {
    const selectedOption = options.find(o => o.value === value);
    const displayValue = selectedOption?.label ?? placeholder ?? 'Select…';

    const handleChange = (event: SelectChangeEvent<string>) => {
        onChange(event.target.value);
    };

    return (
        <Box sx={{ width: '100%' }}>
            {label ? <Typography sx={{ ...editorialLabelSx, mb: 1, display: 'block' }}>{label}</Typography> : null}
            <Box
                sx={{
                    bgcolor: midnight.surfaceHighest,
                    border: '1px solid rgba(255,255,255,0.08)',
                    borderRadius: '12px',
                    minHeight: 52,
                    px: 1.5,
                    py: 0.75,
                    transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
                    '&:hover': {
                        borderColor: 'rgba(255,201,101,0.35)',
                    },
                    '&:focus-within': {
                        boxShadow: '0 0 0 1px rgba(255,201,101,0.35)',
                    },
                }}>
                <FormControl fullWidth disabled={disabled}>
                    <Select
                        variant="standard"
                        disableUnderline
                        value={value}
                        onChange={handleChange}
                        displayEmpty
                        IconComponent={props => (
                            <KeyboardArrowDownIcon
                                {...props}
                                sx={{ color: `${midnight.primary} !important`, fontSize: 22 }}
                            />
                        )}
                        renderValue={() =>
                            selectedOption ? (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0, pr: 3 }}>
                                    <Box
                                        sx={{
                                            width: 30,
                                            height: 30,
                                            borderRadius: '7px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            flexShrink: 0,
                                            bgcolor: 'rgba(255,201,101,0.15)',
                                            color: midnight.primary,
                                            '& .MuiSvgIcon-root': { fontSize: 18 },
                                        }}>
                                        {selectedOption.icon}
                                    </Box>
                                    <Box sx={{ minWidth: 0 }}>
                                        <Typography
                                            noWrap
                                            sx={{
                                                color: midnight.onSurface,
                                                fontSize: 15,
                                                fontWeight: 700,
                                                lineHeight: 1.1,
                                            }}>
                                            {selectedOption.label}
                                        </Typography>
                                        {selectedOption.subtitle ? (
                                            <Typography
                                                noWrap
                                                sx={{
                                                    fontSize: 11,
                                                    color: midnight.onSurfaceVariant,
                                                    lineHeight: 1.2,
                                                }}>
                                                {selectedOption.subtitle}
                                            </Typography>
                                        ) : null}
                                    </Box>
                                </Box>
                            ) : (
                                <Typography sx={{ color: midnight.onSurfaceVariant, fontSize: 15, pr: 3 }}>
                                    {displayValue}
                                </Typography>
                            )
                        }
                        sx={{
                            width: '100%',
                            '& .MuiSelect-select': {
                                display: 'flex',
                                alignItems: 'center',
                                minHeight: '38px !important',
                                py: '0 !important',
                                px: '0 !important',
                            },
                        }}
                        MenuProps={{
                            PaperProps: {
                                sx: {
                                    bgcolor: midnight.surfaceContainer,
                                    borderRadius: '12px',
                                    border: '1px solid rgba(255,255,255,0.08)',
                                    mt: 0.5,
                                    p: 0.75,
                                    maxHeight: 360,
                                    boxShadow: '0 18px 40px rgba(0,0,0,0.55)',
                                    '& .MuiMenuItem-root': {
                                        p: 0,
                                        mb: 0.5,
                                        borderRadius: '8px',
                                        '&:last-of-type': { mb: 0 },
                                        '&:hover': { bgcolor: 'rgba(255,255,255,0.04)' },
                                        '&.Mui-selected': {
                                            bgcolor: 'rgba(255,201,101,0.12)',
                                            '&:hover': { bgcolor: 'rgba(255,201,101,0.18)' },
                                        },
                                    },
                                },
                            },
                        }}>
                        {options.map(option => (
                            <MenuItem key={option.value} value={option.value} sx={{ px: 1.25, py: 1 }}>
                                <OptionRow option={option} selected={option.value === value} />
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Box>
        </Box>
    );
}
